import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { Config, AnalyzeExtensionPointsResult, ExtensionPointAnalysis } from '../types/index.js';
import { AnalyzeExtensionPointsInputSchema } from '../types/index.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { readText, safeJoinPath, fileExists } from '../utils/fs.js';
import { logger } from '../lib/logger.js';
import * as path from 'path';
import * as fs from 'fs/promises';

export const analyzeExtensionPointsTool: Tool = {
  name: 'analyze_extension_points',
  description: `Analyze React pages and components to identify extension points.

Scans TypeScript/React files to detect:
- Forms (suggest form field slots)
- Tables/DataTables (suggest column slots)
- Modals/Dialogs (suggest content slots)
- Headers (suggest action slots)
- Cards/Lists (suggest item slots)

Returns suggested slot names, locations, and context props for each component.
Use this before scaffold_frontend_extension to understand what slots to add.`,
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Path to SmartStack web project (defaults to configured path)',
      },
      target: {
        type: 'string',
        enum: ['pages', 'components', 'forms', 'tables', 'all'],
        default: 'all',
        description: 'Type of components to analyze',
      },
      filter: {
        type: 'string',
        description: 'Filter by file name pattern (e.g., "User*", "*Detail*")',
      },
    },
  },
};

export async function handleAnalyzeExtensionPoints(
  args: unknown,
  config: Config
): Promise<string> {
  try {
    const input = AnalyzeExtensionPointsInputSchema.parse(args);
    const projectPath = input.path || config.smartstack.projectPath;
    const structure = await findSmartStackStructure(projectPath);

    if (!structure.web) {
      return JSON.stringify({
        success: false,
        error: 'Web project folder not found. Expected: web/, client/, or frontend/ directory.',
      });
    }

    const webPath = structure.web;
    const srcPath = safeJoinPath(webPath, 'src');

    // Determine directories to scan based on target
    const directories: string[] = [];
    switch (input.target) {
      case 'pages':
        directories.push(safeJoinPath(srcPath, 'pages'));
        break;
      case 'components':
        directories.push(safeJoinPath(srcPath, 'components'));
        break;
      case 'forms':
        directories.push(safeJoinPath(srcPath, 'pages'));
        directories.push(safeJoinPath(srcPath, 'components'));
        break;
      case 'tables':
        directories.push(safeJoinPath(srcPath, 'pages'));
        directories.push(safeJoinPath(srcPath, 'components'));
        break;
      case 'all':
      default:
        directories.push(safeJoinPath(srcPath, 'pages'));
        directories.push(safeJoinPath(srcPath, 'components'));
        break;
    }

    // Collect all TSX files
    const files: string[] = [];
    for (const dir of directories) {
      if (await fileExists(dir)) {
        const dirFiles = await collectTsxFiles(dir, input.filter);
        files.push(...dirFiles);
      }
    }

    // Analyze each file
    const analyzed: ExtensionPointAnalysis[] = [];
    for (const file of files) {
      const analysis = await analyzeFile(file, srcPath, input.target);
      if (analysis && analysis.suggestedSlots.length > 0) {
        analyzed.push(analysis);
      }
    }

    // Build result
    const result: AnalyzeExtensionPointsResult = {
      totalFiles: files.length,
      analyzed,
      summary: {
        pagesWithForms: analyzed.filter(a => a.hasForm).length,
        pagesWithTables: analyzed.filter(a => a.hasTable).length,
        pagesWithModals: analyzed.filter(a => a.hasModal).length,
        totalSuggestedSlots: analyzed.reduce((sum, a) => sum + a.suggestedSlots.length, 0),
      },
      recommendations: generateRecommendations(analyzed),
    };

    return JSON.stringify(result, null, 2);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger.error('analyze_extension_points failed', { error: err.message });
    return JSON.stringify({
      success: false,
      error: err.message,
    });
  }
}

/**
 * Recursively collect TSX files from a directory
 */
async function collectTsxFiles(dir: string, filter?: string): Promise<string[]> {
  const files: string[] = [];

  try {
    const entries = await fs.readdir(dir, { withFileTypes: true });

    for (const entry of entries) {
      const fullPath = path.join(dir, entry.name);

      if (entry.isDirectory()) {
        // Skip node_modules, test directories, etc.
        if (!['node_modules', '__tests__', 'test', 'tests', '.git'].includes(entry.name)) {
          files.push(...await collectTsxFiles(fullPath, filter));
        }
      } else if (entry.isFile() && entry.name.endsWith('.tsx')) {
        // Skip test files, stories, etc.
        if (!entry.name.includes('.test.') && !entry.name.includes('.spec.') && !entry.name.includes('.stories.')) {
          // Apply filter if provided
          if (filter) {
            const pattern = filter.replace(/\*/g, '.*');
            const regex = new RegExp(`^${pattern}`, 'i');
            if (regex.test(entry.name.replace('.tsx', ''))) {
              files.push(fullPath);
            }
          } else {
            files.push(fullPath);
          }
        }
      }
    }
  } catch (error) {
    // Directory doesn't exist or not accessible
    logger.debug(`Could not read directory: ${dir}`);
  }

  return files;
}

/**
 * Analyze a single React file for extension points
 */
async function analyzeFile(
  filePath: string,
  srcPath: string,
  target: string
): Promise<ExtensionPointAnalysis | null> {
  try {
    const content = await readText(filePath);
    if (!content) return null;

    const relativePath = path.relative(srcPath, filePath).replace(/\\/g, '/');
    const componentName = extractComponentName(content, filePath);
    const isPage = filePath.includes('/pages/') || filePath.includes('\\pages\\');

    const analysis: ExtensionPointAnalysis = {
      file: relativePath,
      componentName,
      type: isPage ? 'page' : 'component',
      suggestedSlots: [],
      hasModal: detectModal(content),
      hasForm: detectForm(content),
      hasTable: detectTable(content),
      imports: extractImports(content),
      lineCount: content.split('\n').length,
    };

    // Generate slot suggestions based on detected patterns
    const slotPrefix = generateSlotPrefix(componentName, relativePath);

    // Filter based on target type
    if (target === 'forms' && !analysis.hasForm) return null;
    if (target === 'tables' && !analysis.hasTable) return null;

    // Header/Actions slots (high priority for pages)
    if (isPage) {
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.header.actions`,
        location: 'Header toolbar/actions area',
        description: 'Add custom action buttons to the page header',
        contextProps: [],
        priority: 'high',
      });
    }

    // Form slots
    if (analysis.hasForm) {
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.form.fields.before`,
        location: 'Before form fields',
        description: 'Add custom fields at the beginning of the form',
        contextProps: ['formData', 'onChange', 'disabled'],
        priority: 'high',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.form.fields.after`,
        location: 'After form fields',
        description: 'Add custom fields at the end of the form',
        contextProps: ['formData', 'onChange', 'disabled'],
        priority: 'high',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.form.actions`,
        location: 'Form action buttons area',
        description: 'Add custom action buttons to the form',
        contextProps: ['formData', 'isSubmitting'],
        priority: 'medium',
      });
    }

    // Table slots
    if (analysis.hasTable) {
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.table.columns.before`,
        location: 'Before standard columns',
        description: 'Add custom columns at the start of the table',
        contextProps: ['tableData'],
        priority: 'high',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.table.columns.after`,
        location: 'After standard columns',
        description: 'Add custom columns at the end of the table',
        contextProps: ['tableData'],
        priority: 'high',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.table.row.actions`,
        location: 'Row actions cell',
        description: 'Add custom action buttons per row',
        contextProps: ['item', 'index'],
        priority: 'medium',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.table.toolbar`,
        location: 'Table toolbar area',
        description: 'Add custom buttons/filters above the table',
        contextProps: ['selectedItems', 'filters'],
        priority: 'medium',
      });
    }

    // Modal/Dialog slots
    if (analysis.hasModal) {
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.modal.content`,
        location: 'Modal body content',
        description: 'Inject custom content into modal',
        contextProps: ['isOpen', 'data'],
        priority: 'medium',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.modal.footer`,
        location: 'Modal footer area',
        description: 'Add custom buttons to modal footer',
        contextProps: ['isOpen', 'onClose'],
        priority: 'low',
      });
    }

    // Card/Detail slots (for detail pages)
    if (isPage && componentName.toLowerCase().includes('detail')) {
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.detail.header`,
        location: 'Detail page header',
        description: 'Add content to detail page header',
        contextProps: ['item'],
        priority: 'high',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.detail.sidebar`,
        location: 'Detail page sidebar',
        description: 'Add sidebar widgets or information',
        contextProps: ['item'],
        priority: 'medium',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.detail.tabs`,
        location: 'Tab navigation',
        description: 'Add custom tabs to detail view',
        contextProps: ['item', 'activeTab'],
        priority: 'medium',
      });
    }

    // List/Card view slots
    if (detectCardView(content)) {
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.card.content`,
        location: 'Card content area',
        description: 'Add custom content to cards',
        contextProps: ['item'],
        priority: 'medium',
      });
      analysis.suggestedSlots.push({
        name: `${slotPrefix}.card.actions`,
        location: 'Card actions area',
        description: 'Add custom action buttons to cards',
        contextProps: ['item'],
        priority: 'medium',
      });
    }

    return analysis;
  } catch (error) {
    logger.debug(`Could not analyze file: ${filePath}`);
    return null;
  }
}

/**
 * Extract component name from file content or filename
 */
function extractComponentName(content: string, filePath: string): string {
  // Try to extract from export default or export function
  const exportMatch = content.match(/export\s+(?:default\s+)?function\s+(\w+)/);
  if (exportMatch) return exportMatch[1];

  const constExportMatch = content.match(/export\s+(?:default\s+)?const\s+(\w+)/);
  if (constExportMatch) return constExportMatch[1];

  // Fall back to filename
  const filename = path.basename(filePath, '.tsx');
  return filename;
}

/**
 * Generate slot prefix from component name
 */
function generateSlotPrefix(componentName: string, _relativePath: string): string {
  // Convert PascalCase to kebab-case
  let prefix = componentName
    .replace(/Page$/, '')
    .replace(/Component$/, '')
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '');

  // Simplify common patterns
  if (prefix.includes('user')) prefix = 'users';
  else if (prefix.includes('role')) prefix = 'roles';
  else if (prefix.includes('permission')) prefix = 'permissions';
  else if (prefix.includes('ticket')) prefix = 'tickets';
  else if (prefix.includes('workflow')) prefix = 'workflows';

  return prefix;
}

/**
 * Detect if file contains a form
 */
function detectForm(content: string): boolean {
  const formPatterns = [
    /<form/i,
    /useForm\(/,
    /onSubmit/,
    /handleSubmit/,
    /<Form/,
    /FormProvider/,
    /useFormContext/,
    /<input/i,
    /<Input/,
    /<TextField/,
    /<Select/,
  ];
  return formPatterns.some(pattern => pattern.test(content));
}

/**
 * Detect if file contains a table
 */
function detectTable(content: string): boolean {
  const tablePatterns = [
    /<table/i,
    /<Table/,
    /DataTable/,
    /DataGrid/,
    /useTable/,
    /<thead/i,
    /<tbody/i,
    /columns=/,
    /columns:/,
  ];
  return tablePatterns.some(pattern => pattern.test(content));
}

/**
 * Detect if file contains a modal/dialog
 */
function detectModal(content: string): boolean {
  const modalPatterns = [
    /<Modal/,
    /<Dialog/,
    /useModal/,
    /useDialog/,
    /isOpen/,
    /onClose/,
    /AlertDialog/,
    /Sheet/,
    /Drawer/,
  ];
  return modalPatterns.some(pattern => pattern.test(content));
}

/**
 * Detect if file contains card view
 */
function detectCardView(content: string): boolean {
  const cardPatterns = [
    /<Card/,
    /\.map\s*\(\s*\([^)]*\)\s*=>\s*</,
    /renderItem/,
    /gridView/,
    /cardView/,
  ];
  return cardPatterns.some(pattern => pattern.test(content));
}

/**
 * Extract import statements
 */
function extractImports(content: string): string[] {
  const imports: string[] = [];
  const importRegex = /import\s+(?:{[^}]+}|\*\s+as\s+\w+|\w+)\s+from\s+['"]([^'"]+)['"]/g;

  let match;
  while ((match = importRegex.exec(content)) !== null) {
    imports.push(match[1]);
  }

  return imports;
}

/**
 * Generate recommendations based on analysis
 */
function generateRecommendations(analyzed: ExtensionPointAnalysis[]): string[] {
  const recommendations: string[] = [];

  const highPriorityCount = analyzed.reduce(
    (sum, a) => sum + a.suggestedSlots.filter(s => s.priority === 'high').length,
    0
  );

  if (highPriorityCount > 0) {
    recommendations.push(
      `Found ${highPriorityCount} high-priority extension points. Consider implementing these first.`
    );
  }

  const pagesWithForms = analyzed.filter(a => a.hasForm && a.type === 'page');
  if (pagesWithForms.length > 0) {
    recommendations.push(
      `${pagesWithForms.length} pages have forms. Add form.fields.after slots for custom fields.`
    );
  }

  const pagesWithTables = analyzed.filter(a => a.hasTable && a.type === 'page');
  if (pagesWithTables.length > 0) {
    recommendations.push(
      `${pagesWithTables.length} pages have tables. Add table.columns.after slots for custom columns.`
    );
  }

  const detailPages = analyzed.filter(a => a.componentName.toLowerCase().includes('detail'));
  if (detailPages.length > 0) {
    recommendations.push(
      `${detailPages.length} detail pages found. Consider adding detail.sidebar and detail.tabs slots.`
    );
  }

  if (analyzed.length === 0) {
    recommendations.push(
      'No extension points found. Try expanding the search with target="all" or adjusting the filter.'
    );
  }

  return recommendations;
}
